/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import {
  ROUTES,
  MOMENT_INPUT_FORMATS,
  ACCESS_CONTROLS,
  UserDataPropTypes,
  ApplicationErrorsPropTypes,
  DashboardPinPropTypes,
  NO_OPENER_REFERRER,
  DOCS_LINKS,
} from 'constants';

import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment';
import { NavLink, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  Header as HeaderComponent,
  HeaderContainer,
  Button,
  SideNav,
  SuiteHeader,
  useUiResources,
} from 'carbon-addons-iot-react';
import {
  Dashboard20,
  Connect20,
  Compass20,
  Application20,
  Group20,
  Help20,
  UserAvatar20,
  Home20,
  ParentChild20,
} from '@carbon/icons-react';
import isNil from 'lodash/isNil';
import useSWR from 'swr';

import HeaderField from '../HeaderField/HeaderField';

import MemberSelection from './components/MemberSelection';

import { handleKeyDown } from 'Utils';
import { generateDashboardURL } from 'monitoringUtils';
import { ErrorNotification } from 'Common';
import { fetchGet, logout } from 'FetchAPIs';
import { hasBetaPreviewEnabled } from 'localStorageUtils';

const messages = defineMessages({
  annCountTooltip:
    '{unreadCount} new {unreadCount, plural, one {statement} other {statements}} since {formattedSinceDate}.',
  announcements: 'Announcements',
  docs: 'Documentation',
  help: 'Help',
  docsDesc: 'Read the product documentation.',
  feedback: 'Feedback',
  feedbackDesc: 'We welcome your feedback and suggestions.',
  reportIssue: 'Report an issue',
  reportIssueDesc: 'Report product issues.',
  userMenu: 'User menu',
  username: 'User name:',
  login: 'Log in',
  showTenantId: `TenantId: {tenantId}`,
  connect: 'Connect',
  setup: 'Setup',
  monitor: 'Monitor',
  usage: 'Services',
  dashboard: 'Dashboard',
  home: 'Home',
  users: 'Users',
  sideNavCloseText: 'Close',
  sideNavOpenText: 'Open',
  about: 'About',
  api: 'API',
  apiDesc: 'Monitor REST APIs Documentation',
  latest: "What's new",
  profileTitle: 'Profile',
  profileManageButton: 'Manage profile',
  profileLogoutButton: 'Log out',
  logout: 'Logout',
  userIcon: 'User',
  settingsIcon: 'Settings',
  profileLogoutModalHeading: 'Do you wish to log out?',
  profileLogoutModalSecondaryButton: 'Cancel',
  profileLogoutModalPrimaryButton: 'Log out',
  profileLogoutModalBody:
    'You are logged in to Maximo Application Suite as {name}.  Logging out also logs you out of each application that is open in the same browser.  To ensure a secure log out, close all open browser windows.',
  switcherNavigatorLink: 'All available applications',
  switcherAppNameMonitor: 'Monitor',
  switcherAppNameHealth: 'Health',
  switcherAppNamePredict: 'Predict',
  switcherAppNameIoT: 'IoT',
  switcherAppNameVisualInspection: 'Visual Inspection',
  openMenu: 'Open Menu',
  sideNav: 'Side navigation bar',
});

const propTypes = {
  className: PropTypes.string,
  /** title shows on the left of header, rest of the properties are each shown on the right */
  title: PropTypes.string.isRequired,
  userData: UserDataPropTypes,
  docURL: PropTypes.string,
  isAnalyticsServiceActive: PropTypes.bool,
  /** optional dropdown errors to show */
  errors: ApplicationErrorsPropTypes,
  onClearError: PropTypes.func.isRequired,
  recentAnnouncementCount: PropTypes.number,
  relatedTenants: PropTypes.arrayOf(PropTypes.string),
  tenantId: PropTypes.string,
  homePagePin: DashboardPinPropTypes,
  userProfileURL: PropTypes.string,
};

const defaultProps = {
  className: null,
  errors: null,
  docURL: null,
  isAnalyticsServiceActive: false,
  userData: null,
  recentAnnouncementCount: null,
  relatedTenants: [],
  tenantId: null,
  homePagePin: null,
  userProfileURL: null,
};

const saasPropTypes = {
  className: PropTypes.string,
  /** title shows on the left of header, rest of the properties are each shown on the right */
  title: PropTypes.string.isRequired,
  userData: UserDataPropTypes,
  docURL: PropTypes.string,
  /** optional dropdown errors to show */
  errors: ApplicationErrorsPropTypes,
  onClearError: PropTypes.func.isRequired,
  recentAnnouncementCount: PropTypes.number,
  relatedTenants: PropTypes.arrayOf(PropTypes.string),
  tenantId: PropTypes.string,
  homePagePin: DashboardPinPropTypes,
  userProfileURL: PropTypes.string,
  links: PropTypes.any,
};

const saasDefaultProps = {
  className: null,
  errors: null,
  docURL: null,
  userData: null,
  recentAnnouncementCount: null,
  relatedTenants: [],
  tenantId: null,
  homePagePin: null,
  userProfileURL: null,
  links: null,
};

const masPropTypes = {
  title: PropTypes.string.isRequired,
  userData: UserDataPropTypes,
  tenantId: PropTypes.string,
  docURL: PropTypes.string,
  links: PropTypes.any,
};

const masDefaultProps = {
  docURL: null,
  userData: null,
  tenantId: null,
  links: null,
};

const MASHeader = ({ title, userData, tenantId, docURL, links }) => {
  const { formatMessage, locale } = useIntl();

  const username = userData ? userData.id : null;

  const customDocLinks = {
    whatsNew: `${docURL}${DOCS_LINKS.HEADERS_WHAT_IS_NEW}`,
    documentation: `${docURL}`,
    gettingStarted: `${docURL}${DOCS_LINKS.HEADERS_GETTING_STARTED}`,
  };

  // Retrieve all the suiteHeaderData
  const [suiteHeaderData] = useUiResources({
    baseApiUrl: `https://api.${userData?.masDomain}`,
    lang: locale,
    surveyId: 'monitor',
    appId: 'monitor',
    workspaceId: tenantId,
  });

  const [refreshInterval, setRefreshInterval] = useState(0);
  const shouldCallRefreshToken = !window.location.href.includes(ROUTES.TIMEOUT) && username;

  useSWR(
    shouldCallRefreshToken ? '/refreshToken' : null,
    url => {
      fetchGet(url)
        .then(response => {
          setRefreshInterval(response.interval ?? 0);
        })
        .catch(err => {
          setRefreshInterval(err?.response?.interval ?? 30 * 1000); // retry in 30 seconds
        });
    },
    {
      refreshInterval,
      revalidateOnFocus: false,
      refreshWhenHidden: true,
      shouldRetryOnError: false, // handle within catch
    }
  );

  return (
    <SuiteHeader
      suiteName={title}
      appName="Monitor"
      userDisplayName={suiteHeaderData?.userDisplayName}
      username={suiteHeaderData?.username}
      routes={{ ...suiteHeaderData?.routes, ...customDocLinks }}
      customHelpLinks={[
        {
          label: formatMessage(messages.api),
          metaData: {
            label: formatMessage(messages.api),
            element: 'a',
            href: ROUTES.DOCS,
            rel: NO_OPENER_REFERRER,
            target: '_blank',
          },
          content: <span>{formatMessage(messages.api)}</span>,
        },
      ]}
      applications={suiteHeaderData?.applications}
      i18n={suiteHeaderData?.i18n}
      surveyData={suiteHeaderData?.surveyData}
      sideNavProps={!isNil(username) ? { links } : null}
      idleTimeoutData={suiteHeaderData?.idleTimeoutData}
      onRouteChange={routeType =>
        new Promise(resolve => {
          // Run custom code before route change
          (async () => {
            if (SuiteHeader.ROUTE_TYPES.LOGOUT === routeType) {
              // clean up the cookie
              await logout();
            }
            // If promise is resolved to true, redirect to the url in the end
            resolve(true);
          })();
        })
      }
    />
  );
};

const SAASHeader = ({
  title,
  userData,
  tenantId,
  docURL,
  relatedTenants,
  recentAnnouncementCount,
  errors,
  onClearError,
  className,
  userProfileURL,
  homePagePin,
  links,
}) => {
  const { formatMessage } = useIntl();
  const history = useHistory();

  const handleLogout = () => {
    history.push('/logout');
  };

  const homePagePinURL = homePagePin
    ? generateDashboardURL({ entityTypeName: homePagePin.resourceDto.name, ...homePagePin }, null)
    : null;
  const username = userData ? userData.id : null;
  const isICP = userData ? userData.isICP : null;

  const userNameContent = username
    ? [
        {
          metaData: {
            element: 'div',
          },
          content: userProfileURL ? (
            <a
              className="user-profile-link"
              href={userProfileURL}
              key="userProfileURL"
              target="_blank"
              rel="noopener noreferrer">
              <HeaderField label={formatMessage(messages.username)} value={username || ''} />
            </a>
          ) : (
            <HeaderField label={formatMessage(messages.username)} value={username || ''} />
          ),
        },
        {
          metaData: {
            element: 'div',
          },
          /* This is some magic id that have a link inserted by our IBM wide cookie analytics */
          content: <div id="teconsent" />,
        },
      ]
    : [];

  const relatedTenantsContent =
    relatedTenants.length > 0
      ? [
          {
            metaData: {
              element: 'div',
              tabIndex: -1,
            },
            content: <MemberSelection memberData={relatedTenants} />,
          },
        ]
      : [];

  const docActionItem = {
    label: 'Docs',
    onClick: null,
    btnContent: <Help20 description={formatMessage(messages.help)} fill="white" />,
    childContent: [
      ...(isICP && userData?.routes?.admin
        ? [
            {
              metaData: {
                element: 'a',
                href: `${userData?.routes?.about}`,
                title: formatMessage(messages.about),
                target: '_blank',
                rel: NO_OPENER_REFERRER,
              },
              content: formatMessage(messages.about),
            },
          ]
        : []),
      {
        metaData: {
          element: 'a',
          href: ROUTES.DOCS,
          title: formatMessage(messages.apiDesc),
          target: '_blank',
          rel: NO_OPENER_REFERRER,
        },
        content: formatMessage(messages.api),
      },
      {
        metaData: {
          element: 'a',
          href: docURL,
          title: formatMessage(messages.docsDesc),
          target: '_blank',
          rel: NO_OPENER_REFERRER,
        },
        content: formatMessage(messages.docs),
      },
      {
        metaData: {
          element: 'a',
          href: `${docURL}${
            isICP ? DOCS_LINKS.HEADERS_WHAT_IS_NEW : DOCS_LINKS.SAAS.HEADERS_WHAT_IS_NEW
          }`,
          title: formatMessage(messages.latest),
          target: '_blank',
          rel: NO_OPENER_REFERRER,
        },
        content: formatMessage(messages.latest),
      },
      {
        metaData: {
          element: 'a',
          href: 'https://ibm-watson-iot.ideas.aha.io/',
          title: formatMessage(messages.feedbackDesc),
          target: '_blank',
          rel: NO_OPENER_REFERRER,
        },
        content: formatMessage(messages.feedback),
      },

      {
        metaData: {
          element: 'a',
          href: `https://www.ibm.com/mysupport/`,
          title: formatMessage(messages.reportIssueDesc),
          target: '_blank',
          rel: NO_OPENER_REFERRER,
        },
        content: formatMessage(messages.reportIssue),
      },
    ],
  };

  const actionItems = [
    docActionItem,
    ...(!isNil(username)
      ? [
          {
            label: 'User',
            onClick: null,
            btnContent: (
              <>
                <span className="header-user-info">
                  {username}
                  <span> {formatMessage(messages.showTenantId, { tenantId })}</span>
                </span>
                <UserAvatar20 fill="white" description="Icon" />
              </>
            ),
            childContent: [
              ...userNameContent,
              ...relatedTenantsContent,
              ...(!isNil(username)
                ? [
                    {
                      metaData: {
                        element: 'div',
                        tabIndex: -1,
                      },
                      content: (
                        <Button
                          key="buttonEnhanced"
                          onClick={() => handleLogout()}
                          onKeyDown={evt => handleKeyDown(evt, handleLogout())}>
                          {formatMessage(messages.profileLogoutButton)}
                        </Button>
                      ),
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  return (
    <>
      <HeaderContainer
        render={({ isSideNavExpanded, onClickSideNavExpand }) => (
          <>
            <HeaderComponent
              className={`header ${className}`}
              appName={title}
              user={username}
              hasSideNav={!!username}
              tenant={tenantId}
              url={homePagePinURL || '/'}
              actionItems={actionItems}
              onClickSideNavExpand={onClickSideNavExpand}
              i18n={{ openMenu: formatMessage(messages.openMenu) }}
            />
            {errors && errors.length > 0
              ? errors.map(error => (
                  <ErrorNotification onClose={onClearError} error={error} key={error.id} />
                ))
              : null}
            {recentAnnouncementCount > 0 && (
              <div
                className="annCount"
                title={formatMessage(messages.annCountTooltip, {
                  unreadCount: recentAnnouncementCount,
                  formattedSinceDate: moment()
                    .subtract(3, 'days')
                    .format(MOMENT_INPUT_FORMATS.DATE_INPUT),
                })}>
                {recentAnnouncementCount}
              </div>
            )}
            {!isNil(username) ? (
              <SideNav
                links={links}
                i18n={{
                  sideNavLabelText: formatMessage(messages.sideNav),
                  closeText: formatMessage(messages.sideNavCloseText),
                  openText: formatMessage(messages.sideNavOpenText),
                }}
                isSideNavExpanded={isSideNavExpanded}
              />
            ) : null}
          </>
        )}
      />
    </>
  );
};

/**
 * The main header for the app.
 */
const Header = ({
  title,
  userData,
  tenantId,
  docURL,
  relatedTenants,
  recentAnnouncementCount,
  isAnalyticsServiceActive,
  errors,
  onClearError,
  className,
  userProfileURL,
  homePagePin,
}) => {
  const { formatMessage } = useIntl();

  const isICP = userData ? userData.isICP : null;

  const { accessControls } = userData || {};

  const links = [
    ...(accessControls?.includes(ACCESS_CONTROLS.MONITOR_HOME_VIEW)
      ? [
          {
            label: formatMessage(messages.home),
            icon: Home20,
            metaData: {
              label: formatMessage(messages.home),
              to: ROUTES.HOME,
              element: NavLink,
              route: ROUTES.HOME,
              'data-testid': 'side-nav-home',
            },
            isEnabled: isAnalyticsServiceActive,
            linkContent: formatMessage(messages.home),
          },
        ]
      : []),
    ...(accessControls?.includes(ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW)
      ? [
          {
            label: formatMessage(messages.dashboard),
            icon: Dashboard20,
            metaData: {
              label: formatMessage(messages.dashboard),
              to: ROUTES.DASHBOARD,
              element: NavLink,
              route: ROUTES.DASHBOARD,
              'data-testid': 'side-nav-dashboard',
            },
            isEnabled: isAnalyticsServiceActive,
            linkContent: formatMessage(messages.dashboard),
          },
        ]
      : []),
    ...(hasBetaPreviewEnabled() &&
    accessControls?.find(accessControl =>
      [
        ACCESS_CONTROLS.MONITOR_CONNECT_VIEW,
        ACCESS_CONTROLS.DEVICE_DEVICES_VIEW,
        ACCESS_CONTROLS.DEVICE_TYPES_VIEW,
        ACCESS_CONTROLS.IM_LOGICAL_INTF_VIEW,
      ].includes(accessControl)
    )
      ? [
          {
            label: formatMessage(messages.connect),
            icon: Connect20,
            metaData: {
              label: formatMessage(messages.connect),
              to: ROUTES.CONNECT,
              element: NavLink,
              route: ROUTES.CONNECT,
              'data-testid': 'side-nav-connect',
            },
            isEnabled: true,
            linkContent: formatMessage(messages.connect),
          },
        ]
      : []),
    ...(accessControls?.find(accessControl =>
      [
        ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
        ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // need to see explore so we can pin dashboards
        ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
        ACCESS_CONTROLS.MONITOR_DATA_VIEW,
      ].includes(accessControl)
    )
      ? [
          {
            label: formatMessage(messages.monitor),
            icon: Compass20,
            metaData: {
              label: formatMessage(messages.monitor),
              to: ROUTES.MONITOR,
              element: NavLink,
              'data-testid': 'side-nav-monitor',
            },
            isEnabled: true,
            linkContent: formatMessage(messages.monitor),
          },
        ]
      : []),
    ...(accessControls?.find(accessControl =>
      [ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE].includes(accessControl)
    )
      ? [
          {
            label: formatMessage(messages.setup),
            icon: ParentChild20,
            metaData: {
              label: formatMessage(messages.setup),
              to: ROUTES.SETUP,
              element: NavLink,
              'data-testid': 'side-nav-setup',
            },
            isEnabled: true,
            linkContent: formatMessage(messages.setup),
          },
        ]
      : []),
    ...(accessControls?.includes(ACCESS_CONTROLS.MONITOR_SERVICES_VIEW)
      ? [
          {
            label: formatMessage(messages.usage),
            icon: Application20,
            metaData: {
              label: formatMessage(messages.usage),
              to: ROUTES.SERVICES,
              element: NavLink,
              route: ROUTES.SERVICES,
              'data-testid': 'side-nav-usage',
            },
            isEnabled: true,
            linkContent: formatMessage(messages.usage),
          },
        ]
      : []),
    // only show users tab if they have access and its not a MAS environment
    // MAS handles user management in a different app
    ...(accessControls?.includes(ACCESS_CONTROLS.MONITOR_USERS_VIEW) && !isICP
      ? [
          {
            label: formatMessage(messages.users),
            icon: Group20,
            metaData: {
              label: formatMessage(messages.users),
              to: ROUTES.USERS,
              element: NavLink,
              route: ROUTES.USERS,
              'data-testid': 'side-nav-users',
            },
            isEnabled: true,
            linkContent: formatMessage(messages.users),
          },
        ]
      : []),
  ];

  return isICP ? (
    <MASHeader
      title={title && title.replace('IBM', '')}
      userData={userData}
      tenantId={tenantId}
      docURL={docURL}
      links={links}
    />
  ) : (
    <SAASHeader
      title={title && title.replace('IBM', '')}
      userData={userData}
      tenantId={tenantId}
      docURL={docURL}
      relatedTenants={relatedTenants}
      recentAnnouncementCount={recentAnnouncementCount}
      errors={errors}
      onClearError={onClearError}
      className={className}
      userProfileURL={userProfileURL}
      homePagePin={homePagePin}
      links={links}
    />
  );
};

SAASHeader.propTypes = saasPropTypes;
SAASHeader.defaultProps = saasDefaultProps;

MASHeader.propTypes = masPropTypes;
MASHeader.defaultProps = masDefaultProps;

Header.propTypes = propTypes;
Header.defaultProps = defaultProps;
export default Header;
